import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/ui/Layout';
import ConfirmationModal from '../../components/ui/ConfirmationModal';


import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../components/AppIcon';
import { useChat } from '../../hooks/useChat';
import toast from 'react-hot-toast';
import { ScenarioCardSkeleton } from '../../components/ui/LoadingSkeleton';


import { useAuth } from '../../contexts/AuthContext';
import { triggerDebtPayoffConfetti } from '../../utils/confetti';
import CelebrationModal from '../../components/ui/CelebrationModal';
import ErrorMessage from '../../components/ui/ErrorMessage';


const DebtPayoffCalculator = () => {
  const { user } = useAuth();
  const [debts, setDebts] = useState([]);
  const [extraPayment, setExtraPayment] = useState(200);
  const [selectedStrategy, setSelectedStrategy] = useState('snowball');
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [customStrategy, setCustomStrategy] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);
  const [previousDebts, setPreviousDebts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteDebtConfirm, setShowDeleteDebtConfirm] = useState(false);
  const [debtToDelete, setDebtToDelete] = useState(null);
  const [deleteDebtLoading, setDeleteDebtLoading] = useState(false);

  const { response, isLoading: aiLoading, error: aiError, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  const retryError = (errorId) => {
    setErrors(errors?.filter(error => error?.id !== errorId));
  };

  const removeError = (errorId) => {
    setErrors(errors?.filter(error => error?.id !== errorId));
  };

  // Load debts from localStorage on mount
  useEffect(() => {
    const savedDebts = localStorage.getItem(`debts_${user?.id}`);
    if (savedDebts) {
      try {
        setDebts(JSON.parse(savedDebts));
      } catch (error) {
        console.error('Failed to parse saved debts:', error);
      }
    }
  }, [user?.id]);

  // Save debts to localStorage whenever they change
  useEffect(() => {
    if (debts?.length > 0 && user?.id) {
      localStorage.setItem(`debts_${user?.id}`, JSON.stringify(debts));
    }
  }, [debts, user?.id]);

  useEffect(() => {
    // Check for debt payoff achievements
    if (debts && debts?.length > 0 && previousDebts?.length > 0) {
      debts?.forEach(debt => {
        const previousDebt = previousDebts?.find(pd => pd?.id === debt?.id);
        
        // Celebrate when a debt is fully paid off (balance reaches 0)
        if (previousDebt && parseFloat(previousDebt?.balance) > 0 && parseFloat(debt?.balance) === 0) {
          triggerDebtPayoffConfetti();
          setCelebrationData({
            debtName: debt?.name,
            originalBalance: previousDebt?.balance,
            type: debt?.type
          });
          setShowCelebration(true);
        }
      });
    }
    setPreviousDebts(debts);
  }, [debts, previousDebts]);

  useEffect(() => {
    if (aiError) {
      toast?.error(aiError?.message);
    }
  }, [aiError]);

  const addDebt = () => {
    const newDebt = {
      id: Date.now(),
      name: '',
      balance: 0,
      interestRate: 0,
      minimumPayment: 0,
      type: 'credit_card'
    };
    setDebts([...debts, newDebt]);
  };

  const updateDebt = (id, field, value) => {
    setDebts(debts?.map(debt => 
      debt?.id === id ? { ...debt, [field]: value } : debt
    ));
  };

  const removeDebt = (id) => {
    const debt = debts?.find(d => d?.id === id);
    setDebtToDelete(debt);
    setShowDeleteDebtConfirm(true);
  };

  // Add this block - missing handler functions
  const handleAddDebt = () => {
    addDebt();
  };

  const handleUpdateDebt = (id, field, value) => {
    updateDebt(id, field, value);
  };

  const handleRemoveDebt = (id) => {
    removeDebt(id);
  };

  const handleShareDebtFree = () => {
    if (celebrationData) {
      const shareText = `I just paid off my ${celebrationData?.debtName}! One step closer to financial freedom! 🎉`;
      if (navigator.share) {
        navigator.share({
          title: 'Debt Paid Off!',
          text: shareText
        })?.catch(err => console.log('Share failed:', err));
      } else {
        navigator.clipboard?.writeText(shareText);
        alert('Debt payoff achievement copied to clipboard!');
      }
    }
  };

  // Calculate payoff schedule helper function (moved before useMemo hooks)
  const calculatePayoffSchedule = (sortedDebts, extra) => {
    let schedule = [];
    let remainingDebts = sortedDebts?.map(d => ({ ...d, remaining: d?.balance }));
    let month = 0;
    let totalInterestPaid = 0;

    while (remainingDebts?.some(d => d?.remaining > 0) && month < 600) {
      month++;
      let availableExtra = extra;

      remainingDebts?.forEach((debt, index) => {
        if (debt?.remaining > 0) {
          const monthlyInterest = (debt?.remaining * (debt?.interestRate / 100)) / 12;
          totalInterestPaid += monthlyInterest;
          
          let payment = debt?.minimumPayment;
          
          // Apply extra payment to first debt with balance
          if (index === remainingDebts?.findIndex(d => d?.remaining > 0) && availableExtra > 0) {
            payment += availableExtra;
            availableExtra = 0;
          }

          const principalPayment = payment - monthlyInterest;
          debt.remaining = Math.max(0, debt?.remaining - principalPayment);
        }
      });

      const totalRemaining = remainingDebts?.reduce((sum, d) => sum + d?.remaining, 0);
      schedule?.push({
        month,
        totalRemaining: parseFloat(totalRemaining?.toFixed(2)),
        totalPaid: parseFloat((debts?.reduce((sum, d) => sum + d?.balance, 0) - totalRemaining)?.toFixed(2))
      });

      if (totalRemaining <= 0) break;
    }

    return {
      schedule,
      totalMonths: month,
      totalInterest: parseFloat(totalInterestPaid?.toFixed(2)),
      totalPaid: parseFloat((debts?.reduce((sum, d) => sum + d?.balance, 0) + totalInterestPaid)?.toFixed(2))
    };
  };

  // Snowball method: Pay off smallest balance first
  const calculateSnowball = useMemo(() => {
    const sortedDebts = [...debts]?.sort((a, b) => a?.balance - b?.balance);
    return calculatePayoffSchedule(sortedDebts, extraPayment);
  }, [debts, extraPayment]);

  // Avalanche method: Pay off highest interest rate first
  const calculateAvalanche = useMemo(() => {
    const sortedDebts = [...debts]?.sort((a, b) => b?.interestRate - a?.interestRate);
    return calculatePayoffSchedule(sortedDebts, extraPayment);
  }, [debts, extraPayment]);

  const getAIRecommendations = async () => {
    const debtSummary = debts?.map(d => 
      `${d?.name}: $${d?.balance} at ${d?.interestRate}% APR, minimum payment $${d?.minimumPayment}`
    )?.join('; ');

    const prompt = `As a financial advisor, analyze these debts and provide a personalized debt payoff strategy with monthly action plan:\n\nDebts: ${debtSummary}\nExtra monthly payment available: $${extraPayment}\n\nProvide:\n1. Recommended strategy (snowball vs avalanche) with reasoning\n2. Month-by-month action plan for first 6 months\n3. Tips to accelerate payoff\n4. Budget optimization suggestions\n\nFormat as clear, actionable advice.`;

    setShowAIRecommendations(true);
    sendMessage([
      { role: 'system', content: 'You are a Canadian financial advisor specializing in debt management and payoff strategies.' },
      { role: 'user', content: prompt }
    ], { max_completion_tokens: 1500 });
  };

  const savings = useMemo(() => {
    return {
      interestSaved: Math.abs(calculateSnowball?.totalInterest - calculateAvalanche?.totalInterest),
      monthsSaved: Math.abs(calculateSnowball?.totalMonths - calculateAvalanche?.totalMonths),
      betterMethod: calculateAvalanche?.totalInterest < calculateSnowball?.totalInterest ? 'avalanche' : 'snowball'
    };
  }, [calculateSnowball, calculateAvalanche]);

  const currentStrategy = selectedStrategy === 'snowball' ? calculateSnowball : calculateAvalanche;

  const handleCalculate = () => {
    if (debts?.length === 0) {
      return;
    }

    setIsCalculating(true);
    
    setTimeout(() => {
      // Calculation logic here
      setIsCalculating(false);
    }, 1000);
  };

  // Add this block - missing scenarios variable
  const scenarios = useMemo(() => {
    if (debts?.length === 0) return [];
    return [
      { name: 'Snowball', data: calculateSnowball },
      { name: 'Avalanche', data: calculateAvalanche }
    ];
  }, [debts, calculateSnowball, calculateAvalanche]);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Debt Payoff Calculator</h1>
            <p className="text-gray-600 mt-1">Calculate and optimize your debt payoff strategy</p>
          </div>

          {/* Error Messages */}
          {errors?.length > 0 && (
            <div className="space-y-3">
              {errors?.map((error) => (
                <ErrorMessage
                  key={error?.id}
                  error={error}
                  onRetry={retryError}
                  onDismiss={removeError}
                />
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {[1, 2]?.map(i => <ScenarioCardSkeleton key={i} />)}
            </div>
          ) : scenarios?.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {scenarios?.map((scenario, index) => (
                <div key={index} className="bg-card rounded-xl border p-4 md:p-6 shadow-elevation-1">
                  {/* Scenario content */}
                </div>
              ))}
            </div>
          )}

          {/* Strategy Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Snowball Method */}
            <div 
              className={`bg-card rounded-xl border p-6 shadow-elevation-1 cursor-pointer transition-all ${
                selectedStrategy === 'snowball' ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedStrategy('snowball')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e?.key === 'Enter' && setSelectedStrategy('snowball')}
              aria-label="Select snowball debt payoff method"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10">
                  <Icon name="TrendingDown" size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Snowball Method</h3>
                  <p className="text-sm text-muted-foreground">Lowest balance first</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Payoff Time:</span>
                  <span className="font-semibold text-foreground">{calculateSnowball?.totalMonths} months</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Interest:</span>
                  <span className="font-semibold text-foreground">${calculateSnowball?.totalInterest?.toLocaleString('en-CA')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Paid:</span>
                  <span className="font-semibold text-foreground">${calculateSnowball?.totalPaid?.toLocaleString('en-CA')}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong>Best for:</strong> Quick wins and motivation. Pay off smallest debts first for psychological boost.
                </p>
              </div>
            </div>

            {/* Avalanche Method */}
            <div 
              className={`bg-card rounded-xl border p-6 shadow-elevation-1 cursor-pointer transition-all ${
                selectedStrategy === 'avalanche' ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setSelectedStrategy('avalanche')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e?.key === 'Enter' && setSelectedStrategy('avalanche')}
              aria-label="Select avalanche debt payoff method"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-success/10">
                  <Icon name="DollarSign" size={24} color="var(--color-success)" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Avalanche Method</h3>
                  <p className="text-sm text-muted-foreground">Highest interest first</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Payoff Time:</span>
                  <span className="font-semibold text-foreground">{calculateAvalanche?.totalMonths} months</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Interest:</span>
                  <span className="font-semibold text-foreground">${calculateAvalanche?.totalInterest?.toLocaleString('en-CA')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Paid:</span>
                  <span className="font-semibold text-foreground">${calculateAvalanche?.totalPaid?.toLocaleString('en-CA')}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <strong>Best for:</strong> Maximum savings. Pay off highest interest debts first to minimize total interest.
                </p>
              </div>
            </div>
          </div>

          {/* Savings Comparison */}
          <div className="bg-gradient-to-r from-success/10 to-primary/10 rounded-xl border border-success/20 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="TrendingUp" size={24} color="var(--color-success)" strokeWidth={2} />
              <h3 className="text-lg font-semibold text-foreground">Potential Savings</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Interest Difference</p>
                <p className="text-2xl font-bold text-success">${savings?.interestSaved?.toLocaleString('en-CA')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Time Difference</p>
                <p className="text-2xl font-bold text-success">{savings?.monthsSaved} months</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recommended Method</p>
                <p className="text-2xl font-bold text-primary capitalize">{savings?.betterMethod}</p>
              </div>
            </div>
          </div>

          {/* Amortization Schedule Chart */}
          <div className="bg-card rounded-xl border p-6 shadow-elevation-1">
            <h3 className="text-lg font-semibold text-foreground mb-4">Payoff Timeline</h3>
            <div className="w-full h-80" role="img" aria-label="Debt payoff timeline chart showing remaining balance over time">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={currentStrategy?.schedule?.filter((_, i) => i % 3 === 0)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(74, 85, 104, 0.1)" />
                  <XAxis 
                    dataKey="month" 
                    label={{ value: 'Months', position: 'insideBottom', offset: -5 }}
                    tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  />
                  <YAxis 
                    label={{ value: 'Balance ($)', angle: -90, position: 'insideLeft' }}
                    tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                    tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'var(--color-card)', border: '1px solid var(--color-border)' }}
                    formatter={(value) => [`$${value?.toLocaleString('en-CA')}`, 'Remaining']}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="totalRemaining" 
                    stroke="var(--color-error)" 
                    strokeWidth={3}
                    name="Remaining Balance"
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="totalPaid" 
                    stroke="var(--color-success)" 
                    strokeWidth={3}
                    name="Amount Paid"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* AI Recommendations */}
          {showAIRecommendations && (
            <div className="bg-card rounded-xl border p-6 shadow-elevation-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10">
                  <Icon name="Sparkles" size={24} color="var(--color-accent)" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">AI-Powered Recommendations</h3>
                  <p className="text-sm text-muted-foreground">Personalized debt payoff strategy</p>
                </div>
              </div>

              {aiLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status" aria-label="Loading AI recommendations"></div>
                </div>
              ) : response ? (
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-foreground">{response}</div>
                </div>
              ) : (
                <p className="text-muted-foreground">Click "AI Optimize" to get personalized recommendations.</p>
              )}
            </div>
          )}

          {/* Monthly Action Plan */}
          <div className="bg-card rounded-xl border p-6 shadow-elevation-1">
            <h3 className="text-lg font-semibold text-foreground mb-4">Monthly Action Plan</h3>
            <div className="space-y-3">
              {debts?.map((debt, index) => {
                const isTarget = selectedStrategy === 'snowball' 
                  ? debt?.balance === Math.min(...debts?.map(d => d?.balance))
                  : debt?.interestRate === Math.max(...debts?.map(d => d?.interestRate));
                
                return (
                  <div key={debt?.id} className={`p-4 rounded-lg ${
                    isTarget ? 'bg-primary/10 border-2 border-primary' : 'bg-muted/30'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-foreground">{debt?.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {isTarget ? (
                            <span className="text-primary font-medium">🎯 Priority: Pay ${debt?.minimumPayment + extraPayment}/month</span>
                          ) : (
                            <span>Pay minimum: ${debt?.minimumPayment}/month</span>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Balance</p>
                        <p className="font-semibold text-foreground">${debt?.balance?.toLocaleString('en-CA')}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <CelebrationModal
          isOpen={showCelebration}
          onClose={() => {
            setShowCelebration(false);
            setCelebrationData(null);
          }}
          title="Debt Paid Off! 🎉"
          message={`Congratulations! You've completely paid off ${celebrationData?.debtName}!`}
          achievementType="debt"
          achievementDetails={celebrationData ? {
            'debt name': celebrationData?.debtName,
            'amount paid': `$${parseFloat(celebrationData?.originalBalance)?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
            type: celebrationData?.type
          } : null}
          nextGoalSuggestion={debts?.filter(d => parseFloat(d?.balance) > 0)?.length > 0 ? 'Keep the momentum going! Focus on your next debt to continue your journey to financial freedom.' : 'You\'re debt-free! Consider redirecting these payments toward savings and investments.'}
          onShare={handleShareDebtFree}
        />

        {/* Delete Debt Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteDebtConfirm}
          onClose={() => {
            setShowDeleteDebtConfirm(false);
            setDebtToDelete(null);
          }}
          onConfirm={() => {
            setShowDeleteDebtConfirm(false);
            setDebts(debts?.filter(debt => debt?.id !== debtToDelete?.id));
            setDebtToDelete(null);
          }}
          title="Remove Debt"
          message={`Are you sure you want to remove "${debtToDelete?.name || 'this debt'}" from the calculator?`}
          warningMessage="This will remove the debt and all its data from your payoff calculation. This action cannot be undone."
          itemDetails={debtToDelete ? {
            name: debtToDelete?.name || 'N/A',
            balance: `$${parseFloat(debtToDelete?.balance || 0)?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
            'interest rate': `${debtToDelete?.interestRate || 0}%`,
            'min payment': `$${parseFloat(debtToDelete?.minimumPayment || 0)?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`
          } : null}
          confirmLabel="Remove Debt"
          cancelLabel="Cancel"
        />
      </div>
    </Layout>
  );
};

export default DebtPayoffCalculator;